import { useAtomCallback } from "jotai/utils";
import { useCallback } from "react";
import { DATA_CHANNEL_MESSAGE_LOGGING_INTERVAL_MS } from "../../config";
import { sendLoggingEvents, TrackingEvents } from "../../events";
import { debounce } from "../../utils";
import type {
  DataChannelAppStateMessage,
  DataChannelMessageSender,
} from "../../video-sdk";
import {
  AudioRecordingState,
  dataChannelAppStateAtom,
  DataChannelMessageType,
  useSendMessage,
  VideoPlatform,
} from "../../video-sdk";
import { useVideoPlatformCallback } from "../../hooks/useVideoPlatform";
import { omit } from "remeda";

const sendDebouncedLoggingEvents = debounce(
  sendLoggingEvents,
  DATA_CHANNEL_MESSAGE_LOGGING_INTERVAL_MS,
);

function toLegacyRecordingProperty(
  recordingState: DataChannelAppStateMessage["participantState"]["recordingState"],
): DataChannelAppStateMessage["participantState"]["isRecording"] {
  return {
    value: recordingState.value === AudioRecordingState.RECORDING,
    lastUpdated: recordingState.lastUpdated,
  };
}

export const useSendParticipantStateCallback = () => {
  const getDataTrackState = useAtomCallback(
    useCallback((get) => get(dataChannelAppStateAtom), []),
  );
  const sendMessage = useSendMessage();
  const getVideoPlatform = useVideoPlatformCallback();

  return useCallback(
    async (senderIdentity: DataChannelMessageSender) => {
      const participantState = getDataTrackState();
      const formattedMessage: DataChannelAppStateMessage = {
        participantState:
          getVideoPlatform() === VideoPlatform.Zoom
            ? omit(participantState, ["isRecording"])
            : {
                ...participantState,
                isRecording: toLegacyRecordingProperty(
                  participantState.recordingState,
                ),
              },
        sender: senderIdentity,
        type: DataChannelMessageType.APP_STATE,
      };
      const didSendMessage = await sendMessage(formattedMessage);
      if (didSendMessage) {
        sendDebouncedLoggingEvents(
          TrackingEvents.REMOTE_DATA_TRACK_MESSAGE_SENT,
          {
            // TODO: TREE-455 - Remove
            isRecording: participantState.isRecording?.value,
            patientConsent: participantState.patient.consent,
            providerConsent: participantState.provider.consent,
            isWhiteboardMode: participantState.isWhiteboardMode,
            recordingState: participantState.recordingState,
          },
          {
            logLevel: "info",
            message:
              "Local participant sent a message on the data track (DEBOUNCED)",
          },
        );
      }
    },
    [getDataTrackState, getVideoPlatform, sendMessage],
  );
};
